import { forwardRef } from 'react';

import { Field } from '@base-ui/react/field';
import { IconButton } from 'components/Button';
import { cn } from 'components/utils';

import {
  textInputAffixRecipe,
  textInputBoxRecipe,
  textInputControlRecipe,
  textInputHelperRecipe,
  textInputLabelRecipe,
  textInputRootRecipe,
  textInputTrailingButtonRecipe,
} from './TextInput.recipe';

import type { TextInputProps } from './types';

/**
 * Design System 2.0 text input — the 60px variant.
 *
 * Ported from the Figma text input matrix (file 5aSkE0U4TeWbuIZDvccHjc, node 447:35126). Props mirror
 * the Figma axes where they are real inputs rather than depictions of state:
 *
 *   `position`   the `Position` axis (Standalone / First / Middle / Last)
 *   `error`      the `hasError` axis
 *   `disabled`   the `isDisabled` axis
 *   `leftIcon`   the `Input Icon` boolean, plus its `↪ Input Icon` swap — a hidden layer inside the
 *                `.Input` row, which is why it does not show up in a screenshot of the set, and
 *                why it is REVEALED here rather than always drawn
 *   `rightButton` the `rightButton` boolean, default `true`, drawn as an ExtraSmall Muted icon-only
 *                Button — so it is rendered as a real `IconButton` and takes a label and a handler
 *                alongside the glyph
 *   `helperText` the `Helper text` boolean, plus whichever of `↪ Helper Text` / `↪ Error text`
 *                applies — the same layer carries both strings, so ONE prop feeds both and `error`
 *                decides which role it plays
 *
 * `isFilled` and `State` are NOT props. Both are observed from the DOM, because Base UI's `Field`
 * already tracks them. All six of Figma's state columns are reachable; the state-matrix note in
 * `TextInput.recipe` has which browser state each one is, and why `Focus` and `Active` are two
 * independent layers rather than two names for the same thing.
 *
 * Theme is not a prop either. Colours resolve from the nearest `.dark` ancestor through the token
 * layer, so the same markup is correct in both themes.
 *
 * The floating label rises and shrinks to 12px once the field is filled or focused. For a field
 * whose label must NOT move, reach for `TextInputPill`.
 *
 * `position` is normally supplied by `FieldGroup` rather than set by hand.
 *
 * The control is the FIRST child and pays all the padding itself, so the `<input>` fills the box and
 * the entire field is a click target. The label and icons are overlaid on top of it — see the note
 * at the top of `TextInput.recipe`. Reordering these children, or moving the padding onto the box,
 * shrinks the hit area back to a 20px strip.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      position,
      leftIcon,
      rightButton,
      rightButtonLabel,
      onRightButtonClick,
      helperText,
      error,
      disabled,
      className,
      rootClassName,
      ...rest
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    /**
     * Narrowed as a pair, exactly as `Alert` narrows its dismiss pair, and for the same reason:
     * destructuring a discriminated union loses the correlation between its members, so each one
     * comes out independently optional. Re-pairing them is what lets `IconButton`'s required
     * `aria-label: string` be satisfied without an assertion.
     *
     * Two checks rather than three. `onRightButtonClick` rides along untested because the type
     * already guarantees it arrives with the other two — and testing it is not merely redundant but
     * an ERROR: `tsc` reports TS2774 on a truthiness check against an always-defined function,
     * reading it as a missing call.
     */
    const trailing = rightButton && rightButtonLabel ? { rightButton, rightButtonLabel, onRightButtonClick } : null;

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the control; every
      // red token below keys off it, so the visual and the announced state cannot drift apart.
      <Field.Root className={cn(textInputRootRecipe(), rootClassName)} disabled={disabled} invalid={error}>
        {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
            without depending on a styling class. The slot classes sit on the root. */}
        <div className={cn(textInputBoxRecipe({ position, hasHelper }), className)} data-part="box">
          <Field.Control
            ref={ref}
            className={textInputControlRecipe({
              hasHelper,
              hasLeftIcon: Boolean(leftIcon),
              hasRightIcon: Boolean(trailing),
            })}
            {...rest}
          />

          <Field.Label className={textInputLabelRecipe({ hasLeftIcon: Boolean(leftIcon) })}>{label}</Field.Label>

          {leftIcon && <span className={textInputAffixRecipe({ side: 'left' })}>{leftIcon}</span>}

          {/**
           * A real `IconButton`, not a styled glyph, because the design draws a Button here and a
           * Button is what has hover and pressed cells. `2xs` is the 24px circle — Figma's
           * `ExtraSmall`, which is NOT the code's `xs`; see `textInputTrailingButtonRecipe`.
           *
           * `type="button"` is explicit: this control sits inside a field that is usually inside a
           * form, and the browser default of `submit` would make a reveal-password toggle submit it.
           */}
          {trailing && (
            <IconButton
              appearance="muted"
              aria-label={trailing.rightButtonLabel}
              className={textInputTrailingButtonRecipe()}
              data-part="trailing-button"
              disabled={disabled}
              fill="default"
              onClick={trailing.onRightButtonClick}
              size="2xs"
              type="button"
            >
              {trailing.rightButton}
            </IconButton>
          )}

          {/**
           * Helper text renders inside the border for this variant, which is how the design draws the
           * Error cells, separated from the value by the box's 4px gap.
           *
           * `Field.Error` needs `match` to render off a caller-supplied flag — without it, it renders
           * only for Base UI's own constraint validation, which this component does not drive. The
           * non-error case uses `Field.Description`; either way the text is wired into
           * `aria-describedby`, so it is announced rather than merely visible.
           */}
          {hasHelper && error && (
            <Field.Error className={textInputHelperRecipe()} match>
              {helperText}
            </Field.Error>
          )}
          {hasHelper && !error && (
            <Field.Description className={textInputHelperRecipe()}>{helperText}</Field.Description>
          )}
        </div>
      </Field.Root>
    );
  }
);
